import { Injectable } from '@angular/core';
import {
	FormationSession,
	FormationSessionInterface,
	FormationSessionPayload,
} from './formation-session';
import { FormationSessionSearchParamsInterface } from './formation-session-search-params';
import { BaseModelService } from '@app/abstracts';
import { environment } from '@env/environment';

// Interfaces for presence endpoint
export interface RespondantMinimal {
	_id: string;
	first_name: string;
	last_name: string;
}

export interface RespondantPresence {
	respondant: RespondantMinimal;
	total_presence: number;
}

export interface PresenceSummaryResponse {
	total_hours: number;
	respondants: RespondantPresence[];
}

@Injectable()
export class FormationSessionService extends BaseModelService<
	FormationSession,
	FormationSessionInterface,
	FormationSessionPayload,
	FormationSessionSearchParamsInterface
> {
	/** @inheritDoc */
	protected path(): string {
		return 'formation-session';
	}
	/** @inheritDoc */
	protected new(object: FormationSessionInterface): FormationSession {
		return new FormationSession(object);
	}

	sendEmailAttestationPresence(
		sessionId: string,
		attestationId: string
	): Promise<void> {
		// Start request
		const options = {
			withCredentials: true,
		};

		return this.http
			.post<void>(
				`${environment.api.uri}/admin/formation-session/${sessionId}/attestation-presence/${attestationId}/send`,
				{},
				options
			)
			.toPromise();
	}

	getPresignedAttestationPresence(
		sessionId: string,
		attestationId: string,
		isAdmin: boolean = false
	): Promise<{
		url: string;
	}> {
		const options = {
			withCredentials: true,
		};
		return this.http
			.get<{
				url: string;
			}>(
				`${this.uri(
					isAdmin
				)}/${sessionId}/attestation-presence/${attestationId}`,
				options
			)
			.toPromise();
	}

	getPresignedAttestationPresenceAsAdmin(
		sessionId: string,
		attestationId: string
	): Promise<{
		url: string;
	}> {
		return this.getPresignedAttestationPresence(
			sessionId,
			attestationId,
			true
		);
	}

	getMetadata(
		sessionId: string,
		isAdmin: boolean = false
	): Promise<{
		total_hours_harmonized: number;
	}> {
		const options = { withCredentials: true };
		return this.http
			.get<{
				total_hours_harmonized: number;
			}>(`${this.uri(isAdmin)}/${sessionId}/metadata`, options)
			.toPromise();
	}

	getMetadataAsAdmin(sessionId: string): Promise<{
		total_hours_harmonized: number;
	}> {
		return this.getMetadata(sessionId, true);
	}

	getPresenceSummary(
		sessionId: string,
		isAdmin: boolean = false
	): Promise<PresenceSummaryResponse> {
		const options = { withCredentials: true };
		return this.http
			.get<PresenceSummaryResponse>(
				`${this.uri(isAdmin)}/${sessionId}/presence`,
				options
			)
			.toPromise();
	}

	getPresenceSummaryAsAdmin(
		sessionId: string
	): Promise<PresenceSummaryResponse> {
		return this.getPresenceSummary(sessionId, true);
	}
}
